import React, {useEffect, useState} from 'react';
import {IonContent, IonModal, IonPage} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import {ApiService} from "../api/ApiService";
import {DocumentInfo} from "../api/model/DocumentsList";
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { BenefitsIcon, ChevronLeft, ChevronRight, FolderDocIcon } from '../components/icons';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';

type DocumentWithPages = DocumentInfo & { pages: number };

const Benefits: React.FC = () => {
    useLogPageVisit('LP Benefits', 'User viewed the Benefits page');
    const history = useHistory();
    const { onSheet, onIonScroll } = useTransparentHeaderContrast();
    const [mounted, setMounted] = useState(false);
    const [loadingDocs, setLoadingDocs] = useState(true);
    const [showModalDoc, setShowModalDoc] = useState(false);
    const [doc, setDoc] = useState<DocumentWithPages>();
    const [selectedDocPages, setSelectedDocPages] = useState<number[]>([]);

    const [docs, setDocList] = useState<DocumentWithPages[]>([]);

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        ApiService.getDocsList('benefits', {isLp: 1, type: 'hr'}).then(it => setDocList(it as DocumentWithPages[])).finally(() => setLoadingDocs(false));
    }, []);

    function openDoc(nextDoc: DocumentWithPages) {
        logUserAction('LP Benefit Doc View', `User viewed benefits document: ${nextDoc.title}`);
        setDoc(nextDoc);
        setSelectedDocPages(Array.from(Array(nextDoc.pages).keys()));
        setShowModalDoc(true);
    }

    const goBack = () => {
      if (history.length > 1) history.goBack();
      else history.replace('/docs');
    };

    const metaLine = loadingDocs ? 'Loading…' : `${docs.length} document${docs.length === 1 ? '' : 's'}`;

    return (
        <IonPage>
            <LoadDetailHeader title="Benefits" onBack={goBack} onSheet={onSheet} />

            <IonContent
              fullscreen
              className="design-a-content design-a-content--light"
              scrollEvents={true}
              onIonScroll={onIonScroll}
            >
                <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                    <div className="home-hero safety-hero">
                        <div className="safety-hero__badge" aria-hidden="true">
                            <BenefitsIcon color="#ffffff" size={28} />
                        </div>
                        <div className="safety-hero__eyebrow">Benefits resources</div>
                        <div className="safety-hero__title home-display">Benefits</div>
                        <div className="safety-hero__meta">{metaLine}</div>
                    </div>

                    <div className="home-sheet safety-sheet">
                        <section className="safety-section">
                            <h2 className="safety-section__title home-section-label">Documents</h2>
                            {docs.length === 0 && !loadingDocs ? (
                                <div className="scoreboard-empty">
                                    <strong>No documents</strong>
                                    <p>Benefits documents will appear here when available.</p>
                                </div>
                            ) : null}
                            <div className="safety-doc-list">
                                {docs.map((item, index) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className="safety-doc-row"
                                        style={{ ['--safety-delay' as string]: `${0.35 + index * 0.04}s` }}
                                        onClick={() => openDoc(item)}
                                    >
                                        <span className="safety-doc-row__icon" aria-hidden="true">
                                            <FolderDocIcon size={18} />
                                        </span>
                                        <span className="safety-doc-row__label">{item.title}</span>
                                        <ChevronRight color="oklch(0.55 0.01 260)" />
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>
                </div>
            </IonContent>

            <IonModal isOpen={showModalDoc} cssClass="safety-viewer-modal" onDidDismiss={() => setShowModalDoc(false)}>
                <div className="safety-media-modal">
                    <div className="safety-media-modal__header">
                        <button type="button" className="safety-media-modal__back" onClick={() => setShowModalDoc(false)}>
                            <ChevronLeft size={20} />
                            Back
                        </button>
                        {doc?.title ? <div className="safety-media-modal__title">{doc.title}</div> : null}
                    </div>
                    <div className="safety-media-modal__body safety-media-modal__body--doc">
                        {selectedDocPages.map(page => (
                            <img
                                key={page}
                                className="safety-media-modal__page"
                                src={doc ? `https://westernexpressmobile.com/docs2/${doc.type}_${doc.id}_${page}.${doc.extension}` : ''}
                                alt={doc ? `${doc.title} page ${page + 1}` : ''}
                            />
                        ))}
                    </div>
                </div>
            </IonModal>
        </IonPage>
    );
};

export default Benefits;
